/**
 * Confirmations humaines adossées à `pending_actions`.
 *
 * Deux responsabilités distinctes vivent ici :
 *
 *   - `ConfirmationPort` — ce que `defineService` interroge juste avant
 *     d'exécuter une action N3. Il **consomme** la confirmation : elle ne vaut
 *     qu'une fois.
 *   - `ConfirmationRepository` — ce dont le domaine a besoin pour préparer,
 *     confirmer, refuser et lister.
 *
 * Les trois mutations passent par des fonctions SECURITY DEFINER plutôt que
 * par un UPDATE direct. Ce n'est pas de la préférence : la table n'a
 * volontairement aucune policy UPDATE, ce qui rend `payload_hash` immuable et
 * la consommation atomique. Un UPDATE depuis le client rouvrirait les deux.
 */
import type {
  ConfirmationPort,
  ConfirmationRepository,
  CreatePendingActionCommand,
  ExecutionContext,
  PendingAction,
  PendingActionStatus,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

/**
 * Le statut n'est pas une colonne : il se déduit des horodatages, qui sont la
 * vérité. Une colonne `status` en plus aurait pu diverger d'eux, et c'est
 * exactement le genre de divergence qu'on ne remarque qu'après coup.
 */
function statusOf(row: Row): PendingActionStatus {
  if (row.consumed_at) return "executee";
  if (row.rejected_at) return "refusee";
  if (row.confirmed_at) {
    // `expires_at` n'est posé qu'à la confirmation, et ne borne que
    // l'exécution. Une demande qui attend n'a pas d'échéance du tout : elle
    // reste sous les yeux du patron jusqu'à ce qu'il tranche.
    const fenetre = row.expires_at ? new Date(row.expires_at).getTime() : 0;
    return fenetre > Date.now() ? "confirmee" : "expiree";
  }
  return "en_attente";
}

function toPendingAction(row: Row): PendingAction {
  return {
    id: row.id,
    action: row.action,
    summary: row.summary,
    payload: row.payload ?? {},
    payloadHash: row.payload_hash,
    status: statusOf(row),
    requestedBy: row.requested_by ?? null,
    createdAt: row.created_at,
    expiresAt: row.expires_at,
  };
}

/**
 * Traduit l'échec d'une RPC de décision.
 *
 * La base répond d'un seul message pour « inexistante », « déjà traitée » et
 * « expirée » — délibérément, pour ne rien apprendre à qui pêche des
 * identifiants. On conserve cette indistinction ici.
 */
function failDecision(error: { message: string; code?: string }): never {
  const raw = error.message ?? "";
  if (raw.includes("forbidden_role")) {
    throw new DomainError(
      "forbidden_role",
      "Seul le patron peut confirmer ou refuser une action à effet externe.",
    );
  }
  if (raw.includes("not_authenticated")) {
    throw new DomainError("not_authenticated", "Session expirée.");
  }
  if (raw.includes("confirmation_expired")) {
    throw new DomainError(
      "confirmation_expired",
      "Cette demande n'attend plus de réponse : elle a expiré, ou elle a déjà été traitée.",
    );
  }
  throw new DomainError("conflict", `Opération refusée par la base : ${raw}`, {
    pgCode: error.code,
  });
}

export function createSupabaseConfirmations(
  clientFor: DbClientFactory,
): ConfirmationPort {
  return {
    async consume(
      ctx: ExecutionContext,
      actionId: string,
      payloadHash: string,
    ): Promise<boolean> {
      const { data, error } = await clientFor(ctx).rpc("consume_pending_action", {
        p_action_id: actionId,
        p_payload_hash: payloadHash,
      });

      // Échec fermé : une base indisponible ne vaut pas une autorisation.
      if (error) return false;
      return data === true;
    },
  };
}

export function createSupabaseConfirmationRepository(
  clientFor: DbClientFactory,
): ConfirmationRepository {
  return {
    async create(
      ctx: ExecutionContext,
      cmd: CreatePendingActionCommand,
    ): Promise<PendingAction> {
      // `tenant_id` est laissé au DEFAULT de la table, qui appelle
      // `public.tenant_id()`. Le transmettre depuis le client reviendrait à
      // laisser l'appelant désigner le tenant chez qui il inscrit l'action.
      const { data, error } = await clientFor(ctx)
        .from("pending_actions")
        .insert({
          action: cmd.action,
          level: cmd.level,
          summary: cmd.summary,
          payload: cmd.payload ?? {},
          payload_hash: cmd.payloadHash,
          requested_by: ctx.actorType === "system" ? null : ctx.actorId,
          actor_type: ctx.actorType,
          channel: ctx.channel,
          request_id: ctx.requestId,
        })
        .select("*")
        .single();

      if (error) failDecision(error);
      return toPendingAction(data as Row);
    },

    async read(ctx: ExecutionContext, id: string): Promise<PendingAction | null> {
      const { data, error } = await clientFor(ctx)
        .from("pending_actions")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error || !data) return null;
      return toPendingAction(data as Row);
    },

    async confirm(ctx: ExecutionContext, id: string): Promise<PendingAction> {
      const { data, error } = await clientFor(ctx).rpc("confirm_pending_action", {
        p_action_id: id,
      });
      if (error) failDecision(error);
      return toPendingAction(data as Row);
    },

    async reject(ctx: ExecutionContext, id: string): Promise<PendingAction> {
      const { data, error } = await clientFor(ctx).rpc("reject_pending_action", {
        p_action_id: id,
      });
      if (error) failDecision(error);
      return toPendingAction(data as Row);
    },

    async listPending(ctx: ExecutionContext): Promise<PendingAction[]> {
      const { data, error } = await clientFor(ctx)
        .from("pending_actions")
        .select("*")
        // Non refusé et non exécuté : ce qui attend encore quelque chose. La
        // condition d'origine ne retenait que les demandes sans réponse, et
        // perdait donc la catégorie qui compte le plus — confirmée, mais dont
        // l'exécution a échoué derrière.
        .is("rejected_at", null)
        .is("consumed_at", null)
        .order("created_at", { ascending: false });

      if (error) failDecision(error);
      return (data ?? []).map((r: Row) => toPendingAction(r));
    },
  };
}
